import { useMemo } from 'react';
import classNames from 'classnames';
import { SortingState, Table } from '@tanstack/react-table';
import { TableHeadCellContent } from 'Components/Table/Table/TableHead/TableHeadCellContent';

interface TableHeaderProps<T> {
  table: Table<T>;
  pinnedLeft: string[];
  pinnedRight: string[];
  sortingState: SortingState;
  enableColumnRemove: boolean;
  onColumnRemove(colId: string): void;
}

export const TableHead = <T extends object>({
  table,
  pinnedLeft,
  pinnedRight,
  sortingState,
  enableColumnRemove,
  onColumnRemove
}: TableHeaderProps<T>) => {
  const pinnedColumns = useMemo((): string[] => [...pinnedLeft, ...pinnedRight], [pinnedLeft, pinnedRight]);

  return (
    <thead className={classNames('table-header')}>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="table-header-row">
          {headerGroup.headers.map((header) => {
            const sortable = header.column.columnDef.sortingFn !== 'auto';
            const canBeRemoved = enableColumnRemove && !pinnedColumns.includes(header.column.id);

            return (
              <th
                key={header.id}
                className={classNames(
                  header.column.id === 'row-select' && 'row-select-cell',
                  pinnedLeft.includes(header.column.id) && 'pinned-left',
                  pinnedRight.includes(header.column.id) && 'pinned-right',
                  sortable ? 'sortable' : ''
                )}
              >
                <TableHeadCellContent
                  sortable={sortable}
                  sortingState={sortingState}
                  header={header}
                  onColumnRemove={canBeRemoved ? onColumnRemove : undefined}
                />
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
};
